import type { DocPageContent } from '../docs-content'

const page = {
  order: 17.5,
  slug: 'mcp-automation',
  title: 'MCP 自动化剪辑安装教程',
  description: '面向外部用户的 FastCut MCP 安装、配置、订阅与 AI 协作完整说明。',
  category: '创意工具',
  sections: [
    {
      title: '它是什么',
      items: [
        'FastCut MCP 让 AI 客户端（如 Codex、ZCode、Cursor）直接读取当前打开的 FastCut 项目状态，并调用剪辑工具自动修改时间线。',
        '面向"本机浏览器 + 本机 MCP 进程 + 本机 AI 客户端"的本地协作场景，所有数据留在本地，无需上传到云端。',
        '当前版本兼容所有支持 stdio MCP 协议的客户端，推荐优先配合 ZCode 或 Codex 使用。',
      ],
    },
    {
      title: '一句话理解工作原理',
      items: [
        '你在浏览器里打开 FastCut 项目 → 本地启动 FastCut MCP Agent → AI 客户端通过 MCP 协议连接 Agent → Agent 将指令转发给浏览器里的 FastCut → 直接操作你当前打开的项目',
        '整个过程不需要你手动导出、导入项目，也不需要把素材上传到任何地方。',
      ],
    },
    {
      title: '开通前先确认',
      items: [
        '请在同一台机器上完成全部操作：浏览器打开 FastCut、AI 客户端在本机运行、MCP Agent 也在本机运行。',
        '请确认你的 FastCut 订阅包含 MCP 自动化权益。MCP 和 AI 能力受订阅限制，手动剪辑和常规导出不受影响。',
        '请先登录 FastCut 账号，并打开一个真实项目。MCP 操作的是"当前浏览器里已经打开的项目"，不是云端项目列表。',
        '推荐使用 Chrome 或 Edge 浏览器，获得最佳兼容性。',
      ],
    },
    {
      title: '快速开始（3 步搞定）',
      items: [
        '1️⃣ 浏览器里打开你的 FastCut 项目。',
        '2️⃣ 进入「编辑器设置」→「MCP 自动化」页面，点击「复制配置」按钮。',
        '3️⃣ 把这段配置粘贴到你的 AI 客户端 MCP 设置里。',
        '完成！AI 客户端会通过 npx 自动下载并运行 FastCut MCP Agent，不需要你手动安装。',
      ],
    },
    {
      title: 'ZCode 配置方法',
      items: [
        '打开 ZCode 设置 → MCP 服务器 → 添加新服务器 → 粘贴刚才复制的 TOML 配置。',
        '保存后，ZCode 会自动启动 FastCut MCP Agent。',
        '第一次启动会稍慢（npx 从 npm 下载包），之后都是秒开。',
        '回到 FastCut 浏览器页面，刷新一下，「MCP 自动化」页面应该显示所有状态都是绿色。',
      ],
    },
    {
      title: 'Codex 配置方法',
      items: [
        '点击「复制给 AI」按钮获得完整提示词。',
        '直接把这段提示词发给 Codex，它会自动理解并帮你配置好 MCP 服务器。',
        '如果本机还没有安装，Codex 也会自动帮你执行安装命令。',
      ],
    },
    {
      title: '其他 MCP 客户端（通用）',
      items: [
        '所有支持 stdio MCP 协议的客户端都可以使用。',
        '配置格式：`command = "npx"，args = ["--yes", "@fastcut-video/mcp-agent"]`。',
        '配置完成后需要重启客户端的 MCP 子进程。',
      ],
    },
    {
      title: '手动安装（开发者模式）',
      items: [
        '如果你想手动管理安装，可以执行：`npm install -g @fastcut-video/mcp-agent`。',
        '验证安装：`fastcut-mcp --doctor` 应该显示 runtime 和 bridgePort 信息。',
        '手动运行 agent：`fastcut-mcp`（保持终端窗口打开）。',
        '当前版本完全不需要设置 `FASTCUT_APP_ROOT` 或本地源码目录。',
      ],
    },
    {
      title: '验证连接状态',
      items: [
        '启动 MCP Agent 后，回到 FastCut 浏览器页面 → 设置 → MCP 自动化。',
        '确认：Bridge 服务「在线」、当前浏览器会话「已连接」、当前项目 ID 正确显示。',
        '所有状态都是绿色 → 连接成功，可以开始使用 AI 自动剪辑了。',
      ],
    },
    {
      title: '给 AI 的最佳实践建议',
      items: [
        '📌 明确告诉 AI：只修改当前打开的 FastCut 项目，不要新建项目，不要导出，不要生成新的外部素材。',
        '📌 让 AI 先调用工具检查当前项目、轨道、时间线和选中状态，再开始修改。',
        '📌 第一次使用时，先让它做一个小的可见修改，比如新增标题、分割片段、调整字幕样式，验证链路正常。',
        '📌 确认稳定后，再逐步尝试更长的自动化剪辑流程。',
      ],
    },
    {
      title: '订阅说明',
      items: [
        'FastCut 订阅规则重点约束 MCP 自动化和 AI 相关能力。',
        '手动剪辑、本地时间线编辑、普通项目管理、常规导出等基础能力不受 MCP 订阅限制。',
        '当你需要通过 MCP / AI 自动操作项目时，需要具备相应的 FastCut MCP/AI 权益。',
        '后台可按用户名搜索用户，开通或关闭 FastCut 订阅；订阅状态会随主站定时同步。',
      ],
    },
    {
      title: '常见问题',
      items: [
        '❓ 显示 Bridge「未连接」：确认 AI 客户端的 MCP 配置已保存并生效，客户端会自动启动 Bridge 进程。',
        '❓ 浏览器会话「未连接」：刷新 FastCut 浏览器页面，让它重新连接本地 Bridge。',
        '❓ 工具调用没有反应：确认当前确实打开了一个真实项目，而不是停留在项目列表页或空编辑器。',
        '❓ 第一次启动很慢：正常，npx 在从 npm 下载包，之后就很快了。',
        '❓ 端口 4174 被占用：可以通过环境变量 `VIDEOCUT_LOCAL_AGENT_PORT` 改成其他端口，记得两边都改。',
        '❓ 不想用了怎么停？在 AI 客户端的 MCP 设置里禁用这个服务器，或者手动杀掉 node 进程。',
      ],
    },
  ],
} satisfies DocPageContent

export default page